import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const type = params.get("type") || "booking";
  const [state, setState] = useState({ status: "checking", data: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setState({ status: "paid", data: res.data });
          return;
        }
        if (res.data.status === "expired") {
          setState({ status: "expired", data: res.data });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "pending", data: res.data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (cancelled) return;
        setState({ status: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="payment-return">
      {state.status === "checking" && (
        <>
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-[#0047FF]" />
          <h1 className="font-display text-3xl mt-6 tracking-tight">Confirming payment…</h1>
          <p className="text-muted-foreground mt-3">Hang tight, this only takes a moment.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div className="w-14 h-14 mx-auto rounded-full bg-[#ac62ea] flex items-center justify-center"><Check className="h-7 w-7 text-white" /></div>
          <h1 className="font-display text-4xl mt-6 tracking-tight">Payment received</h1>
          <p className="text-muted-foreground mt-4">
            {type === "booking" ? "Your session is locked in." : "Your project is now active."} {state.data?.amount_total ? `${fmtUSD(state.data.amount_total / 100)} charged.` : ""}
          </p>
          <Button className="mt-8 rounded-sm bg-[#0A0A0A]" onClick={() => navigate("/dashboard")} data-testid="return-to-dashboard">Go to dashboard</Button>
        </>
      )}
      {state.status === "expired" && (
        <>
          <div className="w-14 h-14 mx-auto rounded-full bg-[#D0021B] flex items-center justify-center"><X className="h-7 w-7 text-white" /></div>
          <h1 className="font-display text-3xl mt-6">Session expired</h1>
          <p className="text-muted-foreground mt-4">No charge was made. You can try again.</p>
          <Button className="mt-8 rounded-sm" onClick={() => navigate("/services")}>Back to services</Button>
        </>
      )}
      {state.status === "pending" && (
        <>
          <h1 className="font-display text-3xl mt-6">Payment processing</h1>
          <p className="text-muted-foreground mt-4">Your payment is still processing. Refresh the dashboard in a minute.</p>
          <Button className="mt-8 rounded-sm" onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
        </>
      )}
      {state.status === "error" && (
        <>
          <h1 className="font-display text-3xl mt-6">Couldn&apos;t verify</h1>
          <p className="text-muted-foreground mt-4">Please contact us if you were charged.</p>
          <Button className="mt-8 rounded-sm" onClick={() => navigate("/")}>Home</Button>
        </>
      )}
    </div>
  );
}
